/**
 * The app's own server, holding the keys.
 *
 * The same contract as `proxy.ts`, at a fixed address on the app's own origin —
 * so there is nothing to configure, no CORS, and no key anywhere near a browser.
 * See `server/assistant/handler.ts` for the other half.
 */
import { ASSISTANT_API, CONTEXT_HEADING, type AskResult, type ServerStatus } from './serverContract';
import type { AskReply, AskRequest, AssistantProvider } from './types';
import { httpError, networkError } from './types';

/**
 * How long a warmed cache is trusted. The model keeps it for five minutes, and
 * every question refreshes it; warming again inside that window buys nothing.
 */
const WARM_FOR_MS = 4 * 60_000;

function stablePart(system: string): string {
  const at = system.indexOf(`\n${CONTEXT_HEADING}\n`);
  return at >= 0 ? system.slice(0, at) : system;
}

export interface ServerChoice {
  /** Whose model will answer — decides whether warming up is worth anything. */
  provider: 'anthropic' | 'openai';
  /** The family's chosen model, or empty for the server's default. */
  model: string;
  /** `quick` | `balanced` | `careful`, or empty for the server's default. */
  effort: string;
}

export function createServerProvider(status: ServerStatus, choice: ServerChoice): AssistantProvider {
  const answer = status.answer;
  const model = choice.model.trim() || undefined;
  const effort = choice.effort.trim() || undefined;
  /** The unchanging part of each prompt the model has read recently, and when. */
  const fresh = new Map<string, number>();
  const touch = (system: string) => fresh.set(stablePart(system), Date.now());

  return {
    id: 'server',
    label: choice.provider === 'openai' ? 'OpenAI' : 'Claude',
    ready: () => !!answer,
    notReadyKey: () => (answer ? null : 'assistant.error.noKey'),

    async ask({ system, turns, tools, signal }: AskRequest): Promise<AskReply> {
      let res: Response;
      try {
        res = await fetch(`${ASSISTANT_API}/ask`, {
          method: 'POST',
          signal,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ system, turns, tools, model, effort }),
        });
      } catch (e) {
        throw networkError(e);
      }
      if (!res.ok) throw await httpError(res);
      touch(system);
      const data = (await res.json()) as Partial<AskResult>;
      return {
        text: (data.text ?? '').trim(),
        calls: (data.calls ?? []).filter((c) => c && typeof c.name === 'string').map((c) => ({ id: c.id ?? c.name, name: c.name, input: c.input ?? {} })),
        providerData: data.providerData,
      };
    },

    /**
     * Pressed Help: have the model read the app map while they are still talking.
     *
     * Only Claude keeps a cache worth filling ahead of time (OpenAI caches by
     * itself from the second question on), and only once per few minutes per
     * mode. Nothing waits on this and nothing reports its failure — it is a head
     * start, not a step.
     */
    warm({ system, tools }) {
      if (choice.provider !== 'anthropic') return;
      const key = stablePart(system);
      const at = fresh.get(key);
      if (at && Date.now() - at < WARM_FOR_MS) return;
      fresh.set(key, Date.now());
      void fetch(`${ASSISTANT_API}/warm`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ system, tools, model }),
      }).catch(() => fresh.delete(key));
    },
  };
}
